/**
 * StructurePlacementModule — interactive deed deployment (Build UX wave).
 *
 * Replaces the Structures panel's blind auto-spot deploy with the standard
 * UGC placement loop (Forge/Fortnite pattern): Deploy enters PLACEMENT MODE —
 * a translucent ghost footprint follows the ground under the cursor, tinted
 * GREEN/RED by a client-side mirror of the server's `placementValidity`
 * (one-structure-per-cell, no-build zones, slope estimate). R / mouse wheel
 * rotates, left-click deploys (the server re-validates — Hard Rule #1),
 * Esc cancels. The mirror is ADVISORY: a red ghost warns before the reducer
 * would reject, it never authorizes.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { sfx } from '../lib/sfx.js';

const CELL_M = 64; // AOI/structure cell size (CLAUDE.md §1)
const SLOPE_WARN = 0.45; // advisory mirror of the server's house slope cap

export class StructurePlacementModule implements IModule {
  readonly name = 'StructurePlacement';

  private scene!: THREE.Scene;
  private canvas!: HTMLCanvasElement;
  private ghost: THREE.Mesh | null = null;
  private ghostMat: THREE.MeshBasicMaterial | null = null;
  private active: { deedId: bigint; structureType: string } | null = null;
  private yawDeg = 0;
  private lastValid = false;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private pointerMove = (e: PointerEvent): void => this.onPointerMove(e);
  private pointerDown = (e: PointerEvent): void => this.onPointerDown(e);
  private wheel = (e: WheelEvent): void => this.onWheel(e);
  private key = (e: KeyboardEvent): void => this.onKey(e);

  init(app: App): void {
    this.scene = app.world.scene;
    this.canvas = app.renderer.native.domElement as HTMLCanvasElement;
    bus.on('atlas:structure-place', ({ deedId, structureType }) => {
      this.start(BigInt(deedId), structureType);
    });
    bus.on('atlas:world-cleared', () => this.cancel());
  }

  private start(deedId: bigint, structureType: string): void {
    this.cancel();
    this.active = { deedId, structureType };
    this.yawDeg = 0;
    atlas.editorCursorMode = true;
    if (document.pointerLockElement) document.exitPointerLock();
    this.ghostMat = new THREE.MeshBasicMaterial({ color: 0x3fb950, transparent: true, opacity: 0.35, depthWrite: false });
    this.ghost = new THREE.Mesh(new THREE.BoxGeometry(10, 6, 10), this.ghostMat);
    this.ghost.visible = false;
    this.scene.add(this.ghost);
    this.canvas.addEventListener('pointermove', this.pointerMove);
    this.canvas.addEventListener('pointerdown', this.pointerDown);
    this.canvas.addEventListener('wheel', this.wheel, { passive: false });
    window.addEventListener('keydown', this.key);
    bus.emit('atlas:status', {
      text: `Placing ${structureType.replace(/_/g, ' ')} — move the cursor, R/wheel rotates, click to deploy, Esc cancels.`,
    });
  }

  private cancel(): void {
    if (!this.active) return;
    this.active = null;
    atlas.editorCursorMode = false;
    if (this.ghost) {
      this.scene.remove(this.ghost);
      this.ghost.geometry.dispose();
      this.ghostMat?.dispose();
      this.ghost = null;
      this.ghostMat = null;
    }
    this.canvas.removeEventListener('pointermove', this.pointerMove);
    this.canvas.removeEventListener('pointerdown', this.pointerDown);
    this.canvas.removeEventListener('wheel', this.wheel);
    window.removeEventListener('keydown', this.key);
  }

  // ─── Ground probe + validity mirror ───────────────────────────────────────────

  private groundHit(e: PointerEvent): THREE.Vector3 | null {
    const cam = atlas.localMesh?.children[0] as THREE.PerspectiveCamera | undefined;
    if (!cam) return null;
    const r = this.canvas.getBoundingClientRect();
    this.pointer.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, cam);
    const hits = this.raycaster.intersectObjects(this.scene.children, true).filter((h) => {
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o === this.ghost) return false; o = o.parent; }
      return true;
    });
    return hits.length ? hits[0].point : null;
  }

  private heightAt(x: number, z: number, nearY: number): number | null {
    this.raycaster.set(new THREE.Vector3(x, nearY + 40, z), new THREE.Vector3(0, -1, 0));
    const hits = this.raycaster.intersectObjects(this.scene.children, true).filter((h) => {
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o === this.ghost) return false; o = o.parent; }
      return true;
    });
    return hits.length ? hits[0].point.y : null;
  }

  /** Client mirror of placementValidity: cell occupancy + no-build zones from
   *  replicated rows, slope from local ray sampling. Advisory only. */
  private validAt(p: THREE.Vector3): { ok: boolean; why: string } {
    const conn = atlas.conn;
    if (!conn) return { ok: false, why: 'not connected' };
    // The rebase shifts the SCENE, not the rows: convert render → world coords.
    const wx = p.x - this.scene.position.x;
    const wz = p.z - this.scene.position.z;
    const cx = Math.floor(wx / CELL_M);
    const cz = Math.floor(wz / CELL_M);
    for (const s of conn.db.structure.iter() as Iterable<{ cellX: number; cellZ: number }>) {
      if (s.cellX === cx && s.cellZ === cz) return { ok: false, why: 'cell already has a structure' };
    }
    const nbz = (conn.db as unknown as { noBuildZone?: { iter(): Iterable<{ cellX: number; cellZ: number }> } }).noBuildZone;
    if (nbz) {
      for (const z of nbz.iter()) {
        if (z.cellX === cx && z.cellZ === cz) return { ok: false, why: 'no-build zone' };
      }
    }
    const h1 = this.heightAt(p.x - 4, p.z, p.y);
    const h2 = this.heightAt(p.x + 4, p.z, p.y);
    const h3 = this.heightAt(p.x, p.z - 4, p.y);
    const h4 = this.heightAt(p.x, p.z + 4, p.y);
    if (h1 !== null && h2 !== null && h3 !== null && h4 !== null) {
      const slope = Math.max(Math.abs(h2 - h1), Math.abs(h4 - h3)) / 8;
      if (slope > SLOPE_WARN) return { ok: false, why: 'too steep' };
    }
    return { ok: true, why: '' };
  }

  // ─── Input ────────────────────────────────────────────────────────────────────

  private onPointerMove(e: PointerEvent): void {
    if (!this.active || !this.ghost) return;
    const p = this.groundHit(e);
    if (!p) { this.ghost.visible = false; return; }
    this.ghost.visible = true;
    this.ghost.position.set(p.x, p.y + 3, p.z);
    this.ghost.rotation.y = THREE.MathUtils.degToRad(this.yawDeg);
    const v = this.validAt(p);
    this.lastValid = v.ok;
    this.ghostMat?.color.setHex(v.ok ? 0x3fb950 : 0xf85149);
  }

  private onPointerDown(e: PointerEvent): void {
    if (!this.active || e.button !== 0 || !this.ghost?.visible) return;
    const conn = atlas.conn;
    if (!conn) return;
    const { deedId, structureType } = this.active;
    if (!this.lastValid) {
      bus.emit('atlas:status', { text: 'Placement is red here — pick flatter, unclaimed ground.' });
      return;
    }
    // Render → world coords (the FP64 rebase shifts scene.position).
    const posX = this.ghost.position.x - this.scene.position.x;
    const posZ = this.ghost.position.z - this.scene.position.z;
    // RADIANS on the wire. WHY (2026-09-14): this sent `yawDeg` raw while the
    // ghost above rendered `degToRad(yawDeg)`, so the preview and the deployed
    // structure disagreed — the renderer, its box collider, the entrance
    // transform and the decoration pass all read `structure.yaw` straight into
    // rotation.y / Euler / Quaternion (StructureRenderModule, DecorationModule).
    // A 45° placement came out at 45 rad ≈ 58°. Rows written before this fix
    // hold degrees and keep rendering the way they always have.
    const yaw = THREE.MathUtils.degToRad(this.yawDeg);
    this.cancel();
    bus.emit('atlas:status', { text: `Deploying ${structureType.replace(/_/g, ' ')}…` });
    void (conn.reducers as unknown as {
      deployStructure(a: { deedId: bigint; posX: number; posZ: number; yaw: number }): Promise<void>;
    })
      .deployStructure({ deedId, posX, posZ, yaw })
      .then(() => { sfx('sfx_structure_place', { gain: 0.5 }); bus.emit('atlas:status', { text: 'Structure deployed.' }); })
      .catch((err: Error) => bus.emit('atlas:status', { text: `Deploy rejected: ${err.message.slice(0, 110)}` }));
  }

  private onWheel(e: WheelEvent): void {
    if (!this.active) return;
    e.preventDefault();
    this.yawDeg = (this.yawDeg + (e.deltaY > 0 ? 15 : -15) + 360) % 360;
    if (this.ghost) this.ghost.rotation.y = THREE.MathUtils.degToRad(this.yawDeg);
  }

  private onKey(e: KeyboardEvent): void {
    if (!this.active) return;
    if (e.code === 'Escape') {
      e.preventDefault();
      this.cancel();
      bus.emit('atlas:status', { text: 'Placement cancelled.' });
    } else if (e.code === 'KeyR') {
      e.preventDefault();
      this.yawDeg = (this.yawDeg + 45) % 360;
      if (this.ghost) this.ghost.rotation.y = THREE.MathUtils.degToRad(this.yawDeg);
    }
  }

  dispose(): void { this.cancel(); }
}
